import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useFormik } from 'formik';
import WarningIcon from '@mui/icons-material/Warning';
import OvRecordEditDialog from './OvRecordEditDialog';
import OvVideoPlacementFields from './OvVideoPlacementFields';
import { AdminVideo } from '../../../common/model/dto/video/admin-video';
import { VideoPlacementValues } from '../../../common/model/type/video-placement.types';
import {
  getPlacementVisibility,
  isPlacementPositionValid,
  toPlacementUpdate,
  toPlacementValues,
} from '../../../common/utils/video-placement-utils';
import { useAdminVideoMutations } from '../../../hooks/use-admin-video-mutations';
import { usePlacementOrderConflict } from '../../../hooks/use-placement-order-conflict';
import { useAudienceLabel } from '../../../hooks/use-audience-label';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvEditVideoPlacementDialogProps {
  video: AdminVideo;
  isOpen: boolean;
  isOrderFocused?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const OvEditVideoPlacementDialog: FC<OvEditVideoPlacementDialogProps> = ({
  video,
  isOpen,
  isOrderFocused = false,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();
  const { update } = useAdminVideoMutations();
  const getAudienceLabel = useAudienceLabel();

  const formik = useFormik<VideoPlacementValues>({
    initialValues: toPlacementValues(video),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!formik.dirty) {
        close();
        return;
      }

      update.mutate(
        { id: video.id, request: toPlacementUpdate(video, values) },
        {
          onSuccess: () => {
            close();
            onSaved();
          },
        }
      );
    },
  });

  const close = () => {
    formik.resetForm();
    update.reset();
    onClose();
  };

  const isValid = isPlacementPositionValid(formik.values);
  const hasOrderConflict = usePlacementOrderConflict(
    video.id,
    formik.values,
    isOpen
  );

  const getVisibilityLabel = () => {
    const visibility = getPlacementVisibility(formik.values);

    switch (visibility.kind) {
      case 'notShown':
        return t('videos.details.visibleToNobody');

      case 'everyone':
        return t('videos.details.visibleTo', {
          audience: getAudienceLabel([]),
        });

      case 'goals':
        return t('videos.details.visibleTo', {
          audience: getAudienceLabel(visibility.goals),
        });

      default: {
        const unhandledVisibility: never = visibility;

        return unhandledVisibility;
      }
    }
  };

  const isPlaced = getPlacementVisibility(formik.values).kind !== 'notShown';

  return (
    <OvRecordEditDialog
      isOpen={isOpen}
      title={t('videos.details.editShownIn')}
      isDirty={formik.dirty}
      isSaving={update.isPending}
      hasError={update.isError}
      isSubmitDisabled={!isValid}
      showLiveNotice
      onSubmit={formik.submitForm}
      onClose={close}
    >
      <OvVideoPlacementFields
        id="video-placement"
        values={formik.values}
        isPositionFocused={isOrderFocused}
        positionError={isValid ? null : t('videos.placement.positionInvalid')}
        onChange={(changes) =>
          formik.setValues({ ...formik.values, ...changes })
        }
      />
      {hasOrderConflict && (
        <Warning role="status">
          <WarningIcon aria-hidden="true" />
          {t('videos.details.duplicateOrder')}
        </Warning>
      )}
      <Summary>
        {getVisibilityLabel()}
        {isPlaced &&
          video.status !== 'ready' &&
          ` ${t('videos.placement.notices.pending')}`}
      </Summary>
    </OvRecordEditDialog>
  );
};

export default OvEditVideoPlacementDialog;

const Warning = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  margin: 0;
  color: ${Colours.OV_ERROR_ACCESSIBLE};
  font-weight: bold;

  .MuiSvgIcon-root {
    flex: none;
    width: 1em;
    height: 1em;
    margin-top: 0.125rem;
  }
`;

const Summary = styled.p`
  margin: 0;
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
`;
